<template>
  <div class="engagement-page">
    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>Content Engagement</h1>
        <p>Monitor user interactions and engagement events across content.</p>
      </div>

      <button class="refresh-btn" @click="refresh">
        <i class="bx bx-refresh"></i>
        Refresh
      </button>
    </div>

    <!-- Stats -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon blue">
          <i class="bx bx-bar-chart-alt-2"></i>
        </div>

        <div>
          <span>Total Events</span>
          <strong>{{ totalEvents }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon green">
          <i class="bx bx-user"></i>
        </div>

        <div>
          <span>Unique Users</span>
          <strong>{{ uniqueUsers }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon purple">
          <i class="bx bx-mouse"></i>
        </div>

        <div>
          <span>Event Types</span>
          <strong>{{ eventTypes }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon orange">
          <i class="bx bx-calendar"></i>
        </div>

        <div>
          <span>Today</span>
          <strong>{{ todayEvents }}</strong>
        </div>
      </div>
    </div>

    <!-- Main Card -->
    <div class="content-card">
      <!-- Toolbar -->
      <div class="toolbar">
        <div class="search-box">
          <i class="bx bx-search"></i>

          <input
            v-model="search"
            type="text"
            placeholder="Search user, session, IP..."
            @keyup.enter="applyFilters"
          />
        </div>

        <select
          v-model="contentTypeFilter"
          class="filter-select"
          @change="applyFilters"
        >
          <option value="">All Content Types</option>

          <option
            v-for="type in contentTypes"
            :key="type"
            :value="type"
          >
            {{ type }}
          </option>
        </select>

        <select
          v-model="eventTypeFilter"
          class="filter-select"
          @change="applyFilters"
        >
          <option value="">All Events</option>

          <option
            v-for="event in eventTypesList"
            :key="event"
            :value="event"
          >
            {{ event }}
          </option>
        </select>

        <select
          v-model="userFilter"
          class="filter-select"
          @change="applyFilters"
        >
          <option value="">All Users</option>

          <option
            v-for="user in userList"
            :key="user.user_id || user.id"
            :value="user.user_id || user.id"
          >
            {{
              user.name ||
              user.user_name ||
              user.email ||
              `User #${user.user_id || user.id}`
            }}
          </option>
        </select>

        <button class="reset-btn" @click="resetFilters">
          <i class="bx bx-reset"></i>
          Reset
        </button>
      </div>

      <!-- Table -->
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Event</th>
              <th>Content Type</th>
              <th>User</th>
              <th>Session</th>
              <th>IP Address</th>
              <th>Date & Time</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="engagement in engagements"
              :key="engagement.eng_id"
              @click="openView(engagement)"
              style="cursor: pointer"
            >
              <!-- Event -->
              <td>
                <div class="event-info">
                  <div class="event-icon">
                    <i class="bx bx-mouse"></i>
                  </div>

                  <div>
                    <strong>{{ engagement.event_type || "-" }}</strong>

                    <small>
                      Event #{{ engagement.eng_id }}
                    </small>
                  </div>
                </div>
              </td>

              <!-- Content Type -->
              <td>
                <span class="content-type-badge">
                  {{ engagement.content_type || "-" }}
                </span>
              </td>

              <!-- User -->
              <td>
                <div class="user-info">
                  <div class="user-avatar">
                    {{
                      engagement.eng_user
                        ? String(engagement.eng_user).substring(0, 2).toUpperCase()
                        : "GU"
                    }}
                  </div>

                  <div>
                    <strong>
                      {{ getUserName(engagement) }}
                    </strong>

                    <small v-if="engagement.eng_user">
                      User #{{ engagement.eng_user }}
                    </small>

                    <small v-else>
                      Guest User
                    </small>
                  </div>
                </div>
              </td>

              <!-- Session -->
              <td>
                <span class="session-text">
                  {{ engagement.eng_session || "—" }}
                </span>
              </td>

              <!-- IP -->
              <td>
                <span class="ip-text">
                  {{ engagement.eng_ip_address || "—" }}
                </span>
              </td>

              <!-- Date -->
              <td>
                <div class="date-info">
                  <strong>
                    {{ formatDate(engagement.eng_datetime) }}
                  </strong>
                </div>
              </td>
            </tr>

            <tr v-if="!loading && engagements.length === 0">
              <td colspan="6" class="empty-state">
                <i class="bx bx-bar-chart-alt-2"></i>
                <h3>No engagement events found</h3>
                <p>Try changing your search or filters.</p>
              </td>
            </tr>

            <tr v-if="loading">
              <td colspan="6" class="empty-state">
                <i class="bx bx-loader-alt bx-spin"></i>
                <h3>Loading...</h3>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination" v-if="totalPages > 1">
        <div class="pagination-info">
          Page
          <strong>{{ page }}</strong>
          of
          <strong>{{ totalPages }}</strong>
        </div>

        <div class="pagination-buttons">
          <button
            :disabled="page === 1"
            @click="changePage(page - 1)"
          >
            <i class="bx bx-chevron-left"></i>
          </button>

          <button
            v-for="pageNumber in totalPages"
            :key="pageNumber"
            :class="{ active: page === pageNumber }"
            @click="changePage(pageNumber)"
          >
            {{ pageNumber }}
          </button>

          <button
            :disabled="page === totalPages"
            @click="changePage(page + 1)"
          >
            <i class="bx bx-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Event Details Modal -->
    <div
      v-if="showModal"
      class="modal-overlay"
      @click.self="closeModal"
    >
      <div class="modal">
        <div class="modal-header">
          <div>
            <h2>Engagement Details</h2>
            <p>Detailed information about this engagement event.</p>
          </div>

          <button
            class="close-btn"
            @click="closeModal"
          >
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div
          class="modal-body"
          v-if="selectedEngagement"
        >
          <div class="detail-event-header">
            <div class="large-event-icon">
              <i class="bx bx-mouse"></i>
            </div>

            <div>
              <h3>
                {{ selectedEngagement.event_type || "-" }}
              </h3>

              <span>
                Event #{{ selectedEngagement.eng_id }}
              </span>
            </div>
          </div>

          <div class="detail-grid">
            <div class="detail-item">
              <label>Content Type</label>
              <span>
                {{ selectedEngagement.content_type || "-" }}
              </span>
            </div>

            <div class="detail-item">
              <label>Event Type</label>
              <span>
                {{ selectedEngagement.event_type || "-" }}
              </span>
            </div>

            <div class="detail-item">
              <label>User ID</label>
              <span>
                {{ selectedEngagement.eng_user || "Guest" }}
              </span>
            </div>

            <div class="detail-item">
              <label>User</label>
              <span>
                {{ getUserName(selectedEngagement) }}
              </span>
            </div>

            <div class="detail-item full">
              <label>Session ID</label>
              <span class="monospace">
                {{ selectedEngagement.eng_session || "—" }}
              </span>
            </div>

            <div class="detail-item full">
              <label>IP Address</label>
              <span class="monospace">
                {{ selectedEngagement.eng_ip_address || "—" }}
              </span>
            </div>

            <div class="detail-item">
              <label>Date & Time</label>
              <span>
                {{ formatDate(selectedEngagement.eng_datetime) }}
              </span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="secondary-btn"
            @click="closeModal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  getEngagementsApi,
  getEngagementStatsApi,
  getEngagementApi
} from "@/apis/engagementapis";

export default {
  name: "ContentEngagement",

  data() {
    return {
      loading: false,

      engagements: [],
      users: [],

      totalEvents: 0,
      uniqueUsers: 0,
      eventTypes: 0,
      todayEvents: 0,

      page: 1,
      limit: 10,
      totalPages: 1,

      search: "",
      contentTypeFilter: "",
      eventTypeFilter: "",
      userFilter: "",

      showModal: false,
      selectedEngagement: null,

      contentTypes: [],
      eventTypesList: [],
      userList: []
    };
  },

  mounted() {
    this.loadStats();
    this.loadEngagements();
  },

  methods: {
    async loadStats() {
      try {
        const res = await getEngagementStatsApi();

        if (res?.status || res?.success) {
          const data = res.data || res;

          this.totalEvents =
            data.totalEvents ??
            data.total_events ??
            data.total ??
            0;

          this.uniqueUsers =
            data.uniqueUsers ??
            data.unique_users ??
            0;

          this.eventTypes =
            data.eventTypes ??
            data.event_types ??
            0;

          this.todayEvents =
            data.todayEvents ??
            data.today_events ??
            data.today ??
            0;
        }
      } catch (error) {
        console.error("Engagement stats error:", error);
      }
    },

    async loadEngagements() {
      this.loading = true;

      try {
        const res = await getEngagementsApi({
          page: this.page,
          limit: this.limit,
          search: this.search,
          content_type: this.contentTypeFilter,
          event_type: this.eventTypeFilter,
          user_id: this.userFilter
        });

        if (res?.status || res?.success) {
          const data = res.data || res;

          this.engagements =
            data.engagements ||
            data.events ||
            data.rows ||
            [];

          const total =
            data.total ??
            data.totalEvents ??
            this.engagements.length;

          this.totalEvents = total;

          this.totalPages =
            data.totalPages ||
            Math.ceil(total / this.limit) ||
            1;

          this.contentTypes =
            data.contentTypes ||
            data.content_types ||
            this.contentTypes ||
            [];

          this.eventTypesList =
            data.eventTypesList ||
            data.event_types_list ||
            data.eventTypes ||
            this.eventTypesList ||
            [];

          this.userList =
            data.users ||
            data.userList ||
            this.userList ||
            [];
        } else {
          this.engagements = [];
          this.totalEvents = 0;
          this.totalPages = 1;
        }
      } catch (error) {
        console.error("Engagement list error:", error);
        this.engagements = [];
      } finally {
        this.loading = false;
      }
    },

    async refresh() {
      await Promise.all([
        this.loadStats(),
        this.loadEngagements()
      ]);
    },

    applyFilters() {
      this.page = 1;
      this.loadEngagements();
    },

    resetFilters() {
      this.search = "";
      this.contentTypeFilter = "";
      this.eventTypeFilter = "";
      this.userFilter = "";

      this.page = 1;
      this.loadEngagements();
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return;

      this.page = page;
      this.loadEngagements();
    },

    async openView(engagement) {
      if (!engagement?.eng_id) return;

      this.loading = true;

      try {
        const res = await getEngagementApi(
          engagement.eng_id
        );

        if (res?.status || res?.success) {
          const data = res.data || res;

          this.selectedEngagement = data;
        } else {
          this.selectedEngagement = engagement;
        }

        this.showModal = true;
      } catch (error) {
        console.error(
          "Engagement view error:",
          error
        );

        this.selectedEngagement = engagement;
        this.showModal = true;
      } finally {
        this.loading = false;
      }
    },

    closeModal() {
      this.showModal = false;
      this.selectedEngagement = null;
    },

    getUserName(engagement) {
      if (!engagement) return "-";

      if (engagement.user_name) {
        return engagement.user_name;
      }

      if (engagement.eng_user) {
        const user = this.userList.find(
          item =>
            String(
              item.user_id ??
              item.id
            ) === String(engagement.eng_user)
        );

        if (user) {
          return (
            user.name ||
            user.user_name ||
            user.email ||
            `User #${engagement.eng_user}`
          );
        }

        return `User #${engagement.eng_user}`;
      }

      return "Guest";
    },

    formatDate(value) {
      if (!value) return "-";

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    }
  }
};
</script>
<style scoped>
.engagement-page {
  min-height: 100%;
  background: #f8fafc;
  padding: 24px;
  color: #0f172a;
}

/* Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* Buttons */
.refresh-btn,
.secondary-btn,
.reset-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s ease;
}

.refresh-btn:hover,
.secondary-btn:hover,
.reset-btn:hover {
  background: #f8fafc;
}

/* Stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
}

.stat-icon.blue {
  background: #eff6ff;
  color: #2563eb;
}

.stat-icon.green {
  background: #f0fdf4;
  color: #16a34a;
}

.stat-icon.purple {
  background: #faf5ff;
  color: #9333ea;
}

.stat-icon.orange {
  background: #fff7ed;
  color: #ea580c;
}

.stat-card span {
  display: block;
  color: #64748b;
  font-size: 12px;
  margin-bottom: 4px;
}

.stat-card strong {
  display: block;
  font-size: 22px;
  line-height: 1;
}

/* Card */
.content-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

/* Toolbar */
.toolbar {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 340px;
}

.search-box i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}

.search-box input,
.filter-select {
  height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  outline: none;
  font-size: 13px;
  color: #334155;
}

.search-box input {
  width: 100%;
  padding: 0 12px 0 40px;
}

.filter-select {
  min-width: 150px;
  padding: 0 12px;
}

.search-box input:focus,
.filter-select:focus {
  border-color: #2563eb;
}

/* Table */
.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1050px;
  border-collapse: collapse;
}

thead {
  background: #f8fafc;
}

th {
  padding: 13px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-top: 1px solid #f1f5f9;
  font-size: 13px;
  color: #475569;
  vertical-align: middle;
}

tbody tr:hover {
  background: #fafcff;
}

/* Event */
.event-info {
  display: flex;
  align-items: center;
  gap: 11px;
}

.event-icon {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.event-info strong {
  display: block;
  color: #1e293b;
  font-size: 13px;
  text-transform: capitalize;
}

.event-info small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 11px;
}

.view-event {
  background: #eff6ff;
  color: #2563eb;
}

.click-event {
  background: #fefce8;
  color: #ca8a04;
}

.enroll-event {
  background: #f0fdf4;
  color: #16a34a;
}

.share-event {
  background: #faf5ff;
  color: #9333ea;
}

.read-event {
  background: #fff7ed;
  color: #ea580c;
}

.default-event {
  background: #f1f5f9;
  color: #64748b;
}

/* Content Type */
.content-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #475569;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}

/* User */
.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
}

.user-info strong {
  display: block;
  color: #334155;
  font-size: 13px;
}

.user-info small {
  display: block;
  margin-top: 2px;
  color: #94a3b8;
  font-size: 10px;
}

.session-text,
.ip-text {
  font-family: monospace;
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

.date-info strong {
  display: block;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.date-info small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 11px;
}

/* Empty */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-state i {
  font-size: 42px;
  color: #cbd5e1;
}

.empty-state h3 {
  margin: 12px 0 5px;
  font-size: 16px;
  color: #334155;
}

.empty-state p {
  margin: 0;
  color: #94a3b8;
}

/* Pagination */
.pagination {
  padding: 14px 16px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}

.pagination-info {
  color: #64748b;
  font-size: 12px;
}

.pagination-buttons {
  display: flex;
  gap: 5px;
}

.pagination-buttons button {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 7px;
  color: #475569;
  cursor: pointer;
}

.pagination-buttons button.active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.pagination-buttons button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal {
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.modal-header h2 {
  margin: 0 0 5px;
  font-size: 18px;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: 0;
  background: #f8fafc;
  border-radius: 7px;
  color: #64748b;
  cursor: pointer;
  font-size: 20px;
}

.modal-body {
  padding: 22px;
  overflow-y: auto;
}

.modal-footer {
  padding: 16px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

/* Detail Header */
.detail-event-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.large-event-icon {
  width: 52px;
  height: 52px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 23px;
}

.detail-event-header h3 {
  margin: 0 0 5px;
  color: #1e293b;
  font-size: 18px;
  text-transform: capitalize;
}

.detail-event-header span {
  color: #94a3b8;
  font-size: 12px;
}

/* Details */
.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-item.full {
  grid-column: 1 / -1;
}

.detail-item label {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.detail-item span {
  color: #475569;
  font-size: 13px;
}

.monospace {
  font-family: monospace;
  word-break: break-all;
}

/* Responsive */
@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .toolbar {
    flex-wrap: wrap;
  }

  .search-box {
    max-width: none;
    width: 100%;
  }
}

@media (max-width: 700px) {
  .engagement-page {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .filter-select {
    flex: 1;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .detail-item.full {
    grid-column: auto;
  }

  .pagination {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
